import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { customersApi } from '../api/customers.api.js';

// Provider HTML contains customer input. Keep it outside the app's origin and
// prevent scripts, forms and remote resources, including in the downloaded file.
const REPORT_CSP =
  "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; style-src 'unsafe-inline'; img-src data:; form-action 'none'; base-uri 'none'\">";

export function CompanyScreeningReportButton({ customerId }: { customerId: string }): JSX.Element {
  return <CustomerVerificationReportButton customerId={customerId} company />;
}

/** Direct access to the provider's report, without opening an onboarding application. */
export function CustomerVerificationReportButton({
  customerId,
  company = false,
  personProviderId,
  subjectName,
}: {
  customerId: string;
  company?: boolean;
  personProviderId?: string;
  subjectName?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const title = company ? 'Company screening report' : 'KYC & screening report';
  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>
        {title}
      </Button>
      {open ? (
        <ReportDialog
          key={`${customerId}:${personProviderId ?? (company ? 'company' : 'applicant')}`}
          customerId={customerId}
          company={company}
          personProviderId={personProviderId}
          title={subjectName ? `${title} — ${subjectName}` : title}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function ReportDialog({
  customerId,
  company,
  personProviderId,
  title,
  onClose,
}: {
  customerId: string;
  company: boolean;
  personProviderId: string | undefined;
  title: string;
  onClose: () => void;
}): JSX.Element {
  const [report, setReport] = useState<{ html: string; url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let url: string | undefined;
    setReport(null);
    setError(null);
    const request = company
      ? customersApi.companyReport(customerId)
      : personProviderId !== undefined
        ? customersApi.partyReport(customerId, personProviderId)
        : customersApi.applicantReport(customerId);
    void request
      .then(async (blob) => {
        const html = REPORT_CSP + (await blob.text());
        if (cancelled) return;
        url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
        setReport({ html, url });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'The provider report could not be loaded.');
      });
    return () => {
      cancelled = true;
      if (url !== undefined) URL.revokeObjectURL(url);
    };
  }, [customerId, company, personProviderId, attempt]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This report uses the provider's existing checks and their original dates. Opening it does
          not request a new screening.
        </Typography>
        {error !== null ? (
          <Alert
            severity="error"
            action={<Button onClick={() => setAttempt((n) => n + 1)}>Retry</Button>}
          >
            {error}
          </Alert>
        ) : report === null ? (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4 }} role="status">
            <CircularProgress size={24} />
            <span>Loading the provider report…</span>
          </Stack>
        ) : (
          <Box
            component="iframe"
            title={`${title} preview`}
            sandbox=""
            srcDoc={report.html}
            sx={{
              width: '100%',
              height: '65vh',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fff',
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {report !== null ? (
          <Button
            component="a"
            href={report.url}
            download={`${company ? 'Company screening report' : 'KYC and screening report'} - ${personProviderId ?? customerId}.html`}
            variant="contained"
          >
            Download report
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
